import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { useAuth } from '../auth/useAuth';
import { firestore } from '../../shared/firebase/client';

export interface Snapshot {
  /** `YYYY-MM-DD` ตามเวลาไทย */
  date: string;
  totalValueTHB: string;
  totalCostTHB: string;
  pnlTHB: string;
  pnlPct: string | null;
  /** วันนั้นดึงราคาได้ไม่ครบ มูลค่าจึงต่ำกว่าความจริง */
  isPartial: boolean;
  /** มูลค่าแยกตามแพลตฟอร์มของวันนั้น — แพลตฟอร์มที่ยังไม่มีในวันนั้นจะไม่มี key */
  byPlatform: Record<string, SnapshotGroup>;
}

export interface SnapshotGroup {
  valueTHB: string;
  isPartial: boolean;
}

/** อ่าน `byPlatform` แบบระวัง — กลุ่มที่รูปร่างผิดถูกข้าม ไม่ทำให้ทั้ง snapshot ใช้ไม่ได้ */
function toGroups(raw: unknown): Record<string, SnapshotGroup> {
  if (typeof raw !== 'object' || raw === null) {
    return {};
  }

  const groups: Record<string, SnapshotGroup> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) {
      continue;
    }
    const record = value as Record<string, unknown>;
    const valueTHB = record['valueTHB'];
    if (typeof valueTHB !== 'string') {
      continue;
    }
    const incompleteCount = record['incompleteCount'];
    groups[key] = {
      valueTHB,
      isPartial: typeof incompleteCount === 'number' && incompleteCount > 0,
    };
  }
  return groups;
}

/** `YYYY-MM-DD` ของวันที่ย้อนหลังไป `days` วัน — ใช้กรองด้วย document id ที่เป็นวันที่อยู่แล้ว */
function daysAgoKey(days: number): string {
  const past = new Date(Date.now() - days * 86_400_000);
  return past.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
}

function toSnapshot(data: Record<string, unknown>): Snapshot | null {
  const date = data['date'];
  const totalValueTHB = data['totalValueTHB'];

  // snapshot ที่ขาดสองค่านี้วาดกราฟไม่ได้ — ข้ามไปดีกว่าวาดจุดที่ไม่รู้ว่าอยู่ตรงไหน
  if (typeof date !== 'string' || typeof totalValueTHB !== 'string') {
    return null;
  }

  return {
    date,
    totalValueTHB,
    totalCostTHB: typeof data['totalCostTHB'] === 'string' ? data['totalCostTHB'] : '0',
    pnlTHB: typeof data['pnlTHB'] === 'string' ? data['pnlTHB'] : '0',
    pnlPct: typeof data['pnlPct'] === 'string' ? data['pnlPct'] : null,
    isPartial: data['isPartial'] === true,
    byPlatform: toGroups(data['byPlatform']),
  };
}

/** `days` เป็น 0 แปลว่าเอาทั้งหมด */
export function useSnapshots(days: number) {
  const { user } = useAuth();
  const uid = user?.uid;

  return useQuery({
    queryKey: ['snapshots', uid, days],
    queryFn: async (): Promise<Snapshot[]> => {
      const ref = collection(firestore, 'users', uid as string, 'snapshots');
      // เรียงตาม `date` ซึ่งเป็น field ที่ทุก snapshot มีเสมอ ไม่ใช่ document id
      const constraints =
        days > 0
          ? [where('date', '>=', daysAgoKey(days)), orderBy('date', 'asc')]
          : [orderBy('date', 'asc')];

      const snapshot = await getDocs(query(ref, ...constraints));
      return snapshot.docs
        .map((doc) => toSnapshot(doc.data()))
        .filter((item): item is Snapshot => item !== null);
    },
    enabled: uid !== undefined,
  });
}
